'use client';

import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Box, ChevronLeft, Loader2, Send } from 'lucide-react';
import type { MyTicketView } from '@/lib/services/support.service';
import { addPending, confirmPending, failPending, type ThreadEntry } from '@/lib/support/thread';
import { formatDateTime, STATUS_LABELS, STATUS_TONES } from '@/lib/support/labels';
import { appendShots, releaseShots, ShotsField, type DraftShot } from './ShotsField';
import { useSupportPanel } from './SupportProvider';

const shotUrl = (id: string) => `/api/support/attachments/${id}`;

/** Le fil d'une de mes demandes (P07) : l'ouvrir marque les reponses lues (R9) ; ma reponse part de facon optimiste */
export function Thread({ number }: { number: number }) {
  const { setThread, refreshUnread, lastLive, setCapturing, isOpen } = useSupportPanel();
  const [ticket, setTicket] = useState<MyTicketView | null>(null);
  const [messages, setMessages] = useState<ThreadEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [body, setBody] = useState('');
  const [shots, setShots] = useState<DraftShot[]>([]);
  const [sendError, setSendError] = useState<string | null>(null);
  const [zoom, setZoom] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const bottom = useRef<HTMLDivElement>(null);

  // Lu a l'ouverture, et relu quand le support repond en direct a cette demande
  const live = lastLive?.ticketNumber === number ? lastLive.at : 0;
  useEffect(() => {
    if (!isOpen) return;
    let gone = false;
    fetch(`/api/support/tickets/${number}`)
      .then(res => (res.ok ? res.json() as Promise<MyTicketView> : Promise.reject(new Error(res.status === 404 ? 'Cette demande est introuvable.' : `erreur ${res.status}`))))
      .then(data => {
        if (gone) return;
        if (!Array.isArray(data?.messages)) throw new Error('réponse illisible');
        setError(null);
        setTicket(data);
        // Mes messages encore en attente restent a leur place
        setMessages(prev => [...data.messages, ...prev.filter(m => m.pending)]);
        refreshUnread();
      })
      .catch(err => { if (!gone) setError(err instanceof Error ? err.message : 'erreur réseau'); });
    return () => { gone = true; };
  }, [number, live, isOpen, refreshUnread, attempt]);
  useEffect(() => { bottom.current?.scrollIntoView({ block: 'end' }); }, [messages.length]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    const sent = shots;
    const { messages: next, pendingId } = addPending(messages, 'Vous', text);
    // Les captures restent visibles dans la bulle pendant l'envoi
    setMessages(next.map(m => (m.id === pendingId ? { ...m, previews: sent.map(x => x.url) } : m)));
    setBody(''); setShots([]); setSendError(null);
    const form = new FormData();
    form.append('body', text);
    appendShots(form, sent);
    try {
      const res = await fetch(`/api/support/tickets/${number}/messages`, { method: 'POST', body: form });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error ?? `erreur ${res.status}`);
      setMessages(prev => confirmPending(prev, pendingId, data as ThreadEntry));
      // R10 : ma reponse rouvre la demande
      setTicket(t => (t ? { ...t, status: 'NEW' } : t));
      releaseShots(sent);
    } catch (err) {
      setMessages(prev => failPending(prev, pendingId).messages);
      // Le texte et les captures reviennent, sans ecraser ce qui a ete tape ou joint pendant l'envoi
      setBody(current => (current.trim() ? current : text));
      setShots(current => (current.length ? (releaseShots(sent), current) : sent));
      setSendError(`Votre réponse n'est pas partie (${err instanceof Error ? err.message : 'connexion perdue'}). Réessayez dans un instant.`);
    }
  };

  return (
    <>
      <div className="grid min-h-0 flex-1 content-start gap-3 overflow-auto px-4 py-3">
        <button type="button" onClick={() => setThread(null)} className="inline-flex items-center gap-1 justify-self-start text-xs font-semibold text-[var(--accent-700)] hover:underline">
          <ChevronLeft size={14} /> Mes demandes
        </button>
        {error ? (
          <p role="alert" className="m-0 text-xs text-rose-700">
            La demande n&apos;a pas pu être chargée ({error}). <button type="button" onClick={() => setAttempt(n => n + 1)} className="font-semibold underline">Réessayer</button>
          </p>
        ) : !ticket ? (
          <div className="grid place-items-center py-8 text-[var(--ink-400)]"><Loader2 size={20} className="animate-spin" /></div>
        ) : (
          <>
            <div>
              <b className="text-[var(--ink-900)]">{ticket.subject}</b>
              <div className="mt-1 flex items-center gap-1.5 text-[11px] text-[var(--ink-500)]">
                <span className={`rounded-full border px-1.5 font-semibold ${STATUS_TONES[ticket.status]}`}>{STATUS_LABELS[ticket.status]}</span> · n° {ticket.number}
              </div>
            </div>
            <div className="grid gap-2">
              {messages.map((m, i) => {
                const mine = m.author === 'USER';
                return (
                  <div key={m.id} className={`max-w-[88%] rounded-2xl px-3 py-2 text-[12.5px] ${mine ? 'justify-self-end bg-[var(--accent)] text-white' : 'justify-self-start bg-[var(--ink-100)] text-[var(--ink-800)]'}`}>
                    <div className={`mb-0.5 text-[10.5px] ${mine ? 'text-blue-100' : 'text-[var(--ink-500)]'}`}>
                      {mine ? 'Vous' : `${m.authorName}, support archifacil`} · {m.pending ? 'Envoi…' : formatDateTime(m.createdAt)}
                    </div>
                    <div className="whitespace-pre-wrap">{m.body}</div>
                    {m.previews && m.previews.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5 opacity-70">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        {m.previews.map(url => <img key={url} src={url} alt="Capture d'écran" className="h-[46px] w-[68px] rounded-md border border-white/40 object-cover" />)}
                      </div>
                    )}
                    {m.attachments.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {m.attachments.map(a => (
                          <button key={a.id} type="button" onClick={() => setZoom(a.id)} title="Agrandir" className="h-[46px] w-[68px] overflow-hidden rounded-md border border-white/40 bg-white">
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img src={shotUrl(a.id)} alt="Capture d'écran" className="h-full w-full object-cover" />
                          </button>
                        ))}
                      </div>
                    )}
                    {i === 0 && ticket.hasSnapshot && <div className="mt-1.5 inline-flex items-center gap-1 text-[10.5px] opacity-85"><Box size={12} /> Copie du projet jointe</div>}
                  </div>
                );
              })}
              {ticket.status === 'RESOLVED' && (
                <p className="m-0 text-center text-[11px] text-[var(--ink-500)]">Demande résolue. Écrivez ci-dessous pour la rouvrir.</p>
              )}
              <div ref={bottom} />
            </div>
          </>
        )}
      </div>
      {ticket && (
        <footer data-shots-scope className="grid gap-2 border-t border-[var(--ink-100)] px-4 py-3">
          {sendError && (
            <div role="alert" className="flex gap-2 rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
              <AlertTriangle size={15} className="mt-0.5 flex-none" /><span>{sendError}</span>
            </div>
          )}
          <ShotsField shots={shots} onChange={setShots} onHide={setCapturing} />
          <div className="flex items-end gap-1.5">
            <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Votre réponse…"
              className="min-h-[40px] flex-1 resize-y rounded-[9px] border border-[var(--ink-200)] px-2.5 py-2 text-[12.5px] outline-none focus:border-[var(--accent)]" />
            <button type="button" onClick={send} disabled={!body.trim()} title="Envoyer"
              className="grid h-10 w-10 place-items-center rounded-[9px] bg-[var(--accent)] text-white hover:bg-[var(--accent-700)] disabled:opacity-50">
              <Send size={15} />
            </button>
          </div>
        </footer>
      )}
      {zoom && (
        <div role="dialog" aria-label="Capture agrandie" className="fixed inset-0 z-[1100] grid place-items-center bg-slate-900/80 p-6" onClick={() => setZoom(null)}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={shotUrl(zoom)} alt="Capture d'écran" className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain" />
        </div>
      )}
    </>
  );
}
